import React, { useState, useEffect } from 'react';
import { Modal, Typography, Space, Checkbox } from 'antd';
import { ThunderboltOutlined } from '@ant-design/icons';

const { Text } = Typography;

export interface SyncStageOption {
  stage: string;
  label: string;
  available: boolean;
}

interface NewFeatureDialogProps {
  visible: boolean;
  onYes: (selectedStages: string[]) => void;
  onNo: () => void;
  onCancel: () => void;
  originStage: string;
  originAlreadyEdited?: boolean;
  availableStages: SyncStageOption[];
}

const NewFeatureDialog: React.FC<NewFeatureDialogProps> = ({
  visible,
  onYes,
  onNo,
  onCancel,
  originStage,
  originAlreadyEdited = false,
  availableStages,
}) => {
  const [selectedStages, setSelectedStages] = useState<string[]>([]);

  // Backend dependency map — what context each stage needs when synced
  // fullstack needs: requirements + API Schema + database
  // database needs: requirements
  // all diagrams need: requirements
  // jiraStories needs: requirements
  const STAGE_DEPENDENCIES: Record<string, string[]> = {
    fullstack: ['requirements', 'diagram:API Schema', 'database'],
    database: ['requirements'],
    'diagram:High-Level System Design': ['requirements'],
    'diagram:Data Flow Diagram': ['requirements'],
    'diagram:Component Diagram': ['requirements'],
    'diagram:API Schema': ['requirements'],
    jiraStories: ['requirements'],
    requirements: [],
  };

  // Compute which stages are required (locked) based on currently selected stages
  const getRequiredStages = (selected: string[]): Set<string> => {
    const required = new Set<string>();
    for (const stage of selected) {
      const deps = STAGE_DEPENDENCIES[stage] || [];
      for (const dep of deps) {
        // Only require it if the stage is available and not the origin
        const isAvailable = availableStages.find(s => s.stage === dep)?.available;
        if (isAvailable && dep !== originStage) {
          required.add(dep);
        }
      }
    }
    return required;
  };

  const requiredStages = getRequiredStages(selectedStages);

  // Effective selection = user selection + required stages
  const effectiveSelection = Array.from(new Set([...selectedStages, ...Array.from(requiredStages)]));

  // Reset selection when dialog opens — pre-select the origin stage
  useEffect(() => {
    if (visible) {
      setSelectedStages([originStage]);
    }
  }, [visible, originStage]);

  const handleCheckboxChange = (stage: string, checked: boolean) => {
    if (stage === originStage) return; // Origin stage cannot be deselected
    if (requiredStages.has(stage)) return; // Required stages cannot be deselected
    setSelectedStages(prev =>
      checked ? [...prev, stage] : prev.filter(s => s !== stage)
    );
  };

  const handleSelectAll = () => {
    setSelectedStages(availableStages.filter(s => s.available).map(s => s.stage));
  };

  const handleDeselectAll = () => {
    // Keep origin stage selected
    setSelectedStages([originStage]);
  };

  const hasOtherSelected = effectiveSelection.some(s => s !== originStage);

  return (
    <Modal
      title={
        <Space>
          <ThunderboltOutlined style={{ color: '#faad14' }} />
          <span>Sync Stages</span>
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      zIndex={1100}
      footer={[
        <button
          key="no"
          onClick={onNo}
          style={{
            padding: '8px 20px',
            marginRight: 8,
            backgroundColor: '#fff',
            border: '1px solid #d9d9d9',
            borderRadius: '6px',
            cursor: 'pointer',
          }}
        >
          No - Edit this stage only
        </button>,
        <button
          key="yes"
          onClick={() => onYes(effectiveSelection)}
          disabled={!hasOtherSelected}
          style={{
            padding: '8px 20px',
            backgroundColor: hasOtherSelected ? '#1890ff' : '#d9d9d9',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            cursor: hasOtherSelected ? 'pointer' : 'not-allowed',
          }}
        >
          Yes - Sync selected stages
        </button>,
      ]}
      width={520}
    >
      <div style={{ padding: '16px 0' }}>
        <Text style={{ fontSize: 16 }}>
          Are you adding a <Text strong>NEW feature or enhancement</Text>?
        </Text>
        <div style={{ marginTop: 12, padding: 12, backgroundColor: '#f6f8fa', borderRadius: 8 }}>
          <Text type="secondary">
            Select which stages should be synced with your changes. The origin stage is already selected.
          </Text>
        </div>

        <div style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
            <Text strong>Stages to sync:</Text>
            <Space size="small">
              <a onClick={handleSelectAll} style={{ fontSize: 12 }}>Select All</a>
              <Text type="secondary" style={{ fontSize: 12 }}>|</Text>
              <a onClick={handleDeselectAll} style={{ fontSize: 12 }}>Deselect All</a>
            </Space>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {availableStages.map(({ stage, label, available }) => {
              const isOrigin = stage === originStage;
              const isRequired = requiredStages.has(stage);
              const isChecked = effectiveSelection.includes(stage);
              const isDisabled = !available || isOrigin || isRequired;
              return (
                <Checkbox
                  key={stage}
                  checked={isChecked}
                  disabled={isDisabled}
                  onChange={e => handleCheckboxChange(stage, e.target.checked)}
                  style={{ marginLeft: 0 }}
                >
                  <Space>
                    <span>{label}</span>
                    {isOrigin && (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {originAlreadyEdited ? '(origin - already edited)' : '(origin - will be edited)'}
                      </Text>
                    )}
                    {isRequired && !isOrigin && (
                      <Text type="secondary" style={{ fontSize: 12, color: '#1890ff' }}>(required for sync context)</Text>
                    )}
                    {!available && !isOrigin && (
                      <Text type="secondary" style={{ fontSize: 12 }}>(not generated yet)</Text>
                    )}
                  </Space>
                </Checkbox>
              );
            })}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default NewFeatureDialog;
